import React, { useState, useRef, useEffect, useMemo } from 'react';
import moment from 'moment';
import { ClipLoader } from 'react-spinners';
import ConfirmationModal from '../../../../../../Components/ConfirmationBox/ConfirmationModal';
import apiClient from '../../../../../../Config/apiClient';
import { showSuccessToast, showErrorToast } from '../../../../../../Context/Toaster/Toaster';
import Pagination from '../../../../../../Utils/Pagination/Pagination';
import editIcon from '../../../../../../Assets/images/edit-icon.svg';
import deleteIcon from '../../../../../../Assets/images/delete-icon.svg';
import searchIcon from '../../../../../../Assets/images/search-icon.svg';
import filterIcon from '../../../../../../Assets/images/filter-icon.svg';
import clearIcon from '../../../../../../Assets/images/clear-icon.svg';
import closeRedIcon from '../../../../../../Assets/images/close-red.svg';
import ViewReplyModal from '../ViewReplyModal/ViewReplyModal';
import { DATE_TIME_FORMAT } from '../../../Utils/Constants';
import './ContactInquiryList.scss';

const contactInquiriesPath = process.env.REACT_APP_HS_CONTACT_INQUIRIES_BASE_PATH;


const ContactInquiryList = ({ inquiries, loading, refreshList }) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [localFilterStatus, setLocalFilterStatus] = useState('');
  const [showFilterDropdown, setShowFilterDropdown] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setShowFilterDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleApplyFilter = (e) => {
    if (e) e.preventDefault();
    setFilterStatus(localFilterStatus);
    setCurrentPage(1);
    setShowFilterDropdown(false);
  };

  const handleClearFilterForm = (e) => {
    if (e) e.preventDefault();
    setLocalFilterStatus('');
  };

  const clearFilters = (e) => {
    if (e) e.preventDefault();
    setFilterStatus('');
    setLocalFilterStatus('');
    setCurrentPage(1);
  };

  const filteredInquiries = useMemo(() => {
    let result = inquiries;

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        (inq) =>
          inq.name.toLowerCase().includes(term) ||
          inq.email.toLowerCase().includes(term)
      );
    }

    if (filterStatus) {
      result = result.filter((inq) => inq.status === filterStatus);
    } else {
      result = result.filter((inq) => inq.status !== 'Archived');
    }

    return result;
  }, [inquiries, searchTerm, filterStatus]);

  const totalPages = Math.ceil(filteredInquiries.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentInquiries = filteredInquiries.slice(startIndex, startIndex + itemsPerPage);

  const handleDelete = async () => {
    try {
      await apiClient.delete(`${contactInquiriesPath}/${deleteId}`);
      showSuccessToast('Inquiry deleted successfully');
      setShowDeleteModal(false);
      setDeleteId(null);
      refreshList();
    } catch (err) {
      showErrorToast(err?.response?.data?.error_data?.message || 'Failed to delete inquiry');
    }
  };

  const handleView = (inquiry) => {
    setSelectedInquiry(inquiry);
    setShowViewModal(true);
  };

  return (
    <main className="full-wraper">
      <div className="main-wraper">
        <div className="container-fluid">
          <div className="main-inner">
            <div className="common-top exm-top">
              <div className="filter-cvr" ref={filterRef}>
                <a
                  className={`filter-btn ${filterStatus ? 'active-filter' : ''}`}
                  href="#"
                  onClick={(e) => { e.preventDefault(); setShowFilterDropdown((prev) => !prev); }}
                >
                  <span className="filter-icon-wrapper">
                    <img className="ct-icon" src={filterIcon} alt="filter" />
                    {filterStatus && <span className="filter-indicator-dot"></span>}
                  </span>
                  Filter
                </a>
                <a className="clear-btn" href="#" onClick={clearFilters}>
                  <img className="ct-icon" src={clearIcon} alt="clear" />
                  Clear Filter
                </a>

                {showFilterDropdown && (
                  <ul className="dropdown-menu filterdrop-body show dropstyle">
                    <div className="filterdrop-split">
                      <label className="filter-label">
                        Status
                        {localFilterStatus && (
                          <img className="red-close" src={closeRedIcon} alt="clear" onClick={() => setLocalFilterStatus('')} style={{ cursor: 'pointer' }} />
                        )}
                      </label>
                      <select
                        className="filter-select"
                        value={localFilterStatus}
                        onChange={(e) => setLocalFilterStatus(e.target.value)}
                      >
                        <option value="">--select--</option>
                        <option value="New">New</option>
                        <option value="Read">Read</option>
                        <option value="Replied">Replied</option>
                        <option value="Archived">Archived</option>
                      </select>
                    </div>

                    <div className="filterdrop-split mb-0">
                      <div className="filter-buttons">
                        <a href="#" className="filter-clear" onClick={handleClearFilterForm}>Clear</a>
                        <a href="#" className="filter-apply" onClick={handleApplyFilter}>Apply</a>
                      </div>
                    </div>
                  </ul>
                )}
              </div>
              <div className="search-bar">
                <form onSubmit={(e) => e.preventDefault()}>
                  <input
                    className="form-control me-2"
                    type="search"
                    placeholder="Search by name or email"
                    value={searchTerm}
                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  />
                  <button className="ser-submit" type="submit">
                    <img src={searchIcon} alt="search" />
                  </button>
                </form>
              </div>
            </div>

            <div className="table-cvr">
              <div className="table-inner">
                <table className="table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>Submitted</th>
                      <th></th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {loading ? (
                      <tr>
                        <td colSpan="8" className="text-center py-4">
                          <ClipLoader size={30} color="gray" />
                        </td>
                      </tr>
                    ) : currentInquiries.length > 0 ? (
                      currentInquiries.map((inquiry, index) => (
                        <tr key={inquiry.id}>
                          <td>{startIndex + index + 1}</td>
                          <td>{inquiry.name}</td>
                          <td>{inquiry.email}</td>
                          <td>
                            {inquiry.message && inquiry.message.length > 50
                              ? `${inquiry.message.substring(0, 50)}...`
                              : inquiry.message || ''}
                          </td>
                          <td>
                            <p className={inquiry.status ? inquiry.status.toLowerCase().replace(/\s+/g, '-') : ''}>
                              {inquiry.status}
                            </p>
                          </td>
                          <td>{moment(inquiry.submitted_at).local().format(DATE_TIME_FORMAT)}</td>
                          <td className="edit-col text-center">
                            <a href="#" onClick={(e) => { e.preventDefault(); handleView(inquiry); }}>
                              <img className="tb-icon" src={editIcon} alt="View" />
                            </a>
                          </td>
                          <td className="edit-col text-center">
                            <a href="#" onClick={(e) => { e.preventDefault(); setDeleteId(inquiry.id); setShowDeleteModal(true); }}>
                              <img className="tb-icon" src={deleteIcon} alt="Delete" />
                            </a>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan="8" className="text-center py-4 text-muted">
                          No inquiries found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredInquiries.length}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                onPageChange={setCurrentPage}
              />
            </div>

            <ViewReplyModal
              show={showViewModal}
              inquiry={selectedInquiry}
              onClose={() => setShowViewModal(false)}
              onUpdate={refreshList}
            />

            <ConfirmationModal
              id="deleteInquiryModal"
              show={showDeleteModal}
              title="Delete Inquiry"
              message="Are you sure you want to delete this inquiry?"
              confirmText="Delete"
              cancelText="Cancel"
              onConfirm={handleDelete}
              onCancel={() => setShowDeleteModal(false)}
            />
          </div>
        </div>
      </div>
    </main>
  );
};

export default ContactInquiryList;
